import React, { useState } from 'react';
import {
    Box,
    Typography,
    Grid,
    Paper,
    Divider,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Chip,
    Card,
    CardContent,
    Link,
    Button
} from '@mui/material';

import {
    HomeOutlined,
    GavelOutlined,
    TrendingUpOutlined,
    CheckCircleOutline,
    WarningAmberOutlined,
    ArrowForwardIos,
    PeopleAltOutlined,
    ReceiptLongOutlined,
    QuestionAnswerOutlined
} from '@mui/icons-material';

// スクロール用の関数
const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
        const offset = 80; // ヘッダーの高さ分のオフセット
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;

        window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
        });
    }
};

const tocItems = [
    { id: 'introduction', label: 'はじめに' },
    { id: 'flow', label: '契約更新の基本的な流れ' },
    { id: 'negotiation', label: '家賃交渉のコツ' },
    { id: 'decision', label: '退去か継続か？判断のポイント' },
    { id: 'trouble', label: '契約更新トラブルを回避するには' },
    { id: 'faq', label: 'よくある質問・Q&A' },
    { id: 'summary', label: 'まとめ' },
];

const PostContent = () => {
    const [showToc, setShowToc] = useState(true);
    return (
        <Box sx={{ maxWidth: 800, mx: 'auto', px: { xs: 1, sm: 3 } }}>
            {showToc ? (
                <Card sx={{ mb: 4, boxShadow: 2, bgcolor: '#e3f2fd', border: '1px solid #ddd', borderRadius: 2 }}>
                    <CardContent sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <Typography variant="h6" sx={{ flexGrow: 1 }}>目次</Typography>
                            <Button variant="outlined" size="small" onClick={() => setShowToc(false)}>
                                閉じる
                            </Button>
                        </Box>
                        <List sx={{ p: 0 }}>
                            {tocItems.map((item, idx) => (
                                <ListItem key={item.id} sx={{ p: 0.5, display: 'flex', alignItems: 'center' }}>
                                    <Box sx={{
                                        minWidth: 32,
                                        minHeight: 32,
                                        bgcolor: '#f5f5f5',
                                        color: '#888',
                                        fontWeight: 'bold',
                                        borderRadius: 1,
                                        fontSize: '1.15rem',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        mr: 1
                                    }}>
                                        {idx + 1}.
                                    </Box>
                                    <Link component="button" onClick={() => scrollToSection(item.id)}
                                        color="inherit" underline="hover"
                                        sx={{ cursor: 'pointer', fontSize: '0.98rem', textAlign: 'left' }}
                                    >
                                        {item.label}
                                    </Link>
                                </ListItem>
                            ))}
                        </List>
                    </CardContent>
                </Card>
            ) : (
                <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setShowToc(true)}
                    sx={{ mb: 2 }}
                >
                    目次を表示
                </Button>
            )}

            {/* 導入 */}
            <Box id="introduction" sx={{ mb: 5 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    はじめに
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    ルームシェア生活に慣れてくると、意外と意識しないまま迎えてしまうのが「契約更新」のタイミングです。
                    <Box component="span" sx={{ fontWeight: 'bold', color: 'primary.main' }}>契約更新</Box>は、新たな生活のスタートラインであり、時には大きな分岐点にもなります。
                    <br />
                    「家賃は上がるの？」「更新料や手数料は？」「住み続けるべき？」そんな疑問や不安に、<Box component="span" sx={{ fontWeight: 'bold' }}>ルームシェア専門家</Box>の視点から徹底的にお答えします。
                </Typography>
                <Paper elevation={0} sx={{ p: 2, bgcolor: '#fafafa', mb: 2, borderRadius: 2 }}>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={6}>
                            <img
                                src="/images/blog/living-renewal_roomshare-keiyaku-koushin-point.jpg"
                                alt="明るい共有リビングでルームメイトが契約書を囲みながら相談している場面。テーブルの上にはカラフルなマグカップとノートパソコン、窓からは柔らかな自然光が差し込んでおり、穏やかなコミュニケーションの雰囲気が伝わる"
                                style={{ width: '100%', borderRadius: 8, objectFit: 'cover' }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="text.secondary">
                                契約更新は「次の1〜2年も快適に暮らす」ための大切な節目です。この記事では、<b>スムーズな更新</b>のために押さえるべき流れとポイント、家賃交渉やトラブル回避のコツまで、実践的に解説します。
                            </Typography>
                        </Grid>
                    </Grid>
                </Paper>
                <Typography variant="body2" sx={{ mt: 1 }}>
                    <Chip label="契約更新" color="primary" variant="outlined" sx={{ mr: 1 }} />
                    <Chip label="家賃交渉" color="primary" variant="outlined" sx={{ mr: 1 }} />
                    <Chip label="トラブル回避" color="primary" variant="outlined" />
                </Typography>
            </Box>

            {/* 契約更新の流れ */}
            <Box id="flow" sx={{ mb: 5 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    契約更新の基本的な流れ
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ mb: 2 }}>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        ほとんどの物件では、<b>1～2年ごとに契約更新</b>が必要です。スムーズな更新のためには、<b>事前準備と流れ</b>を押さえておきましょう。
                    </Typography>
                    <List>
                        <ListItem>
                            <ListItemIcon><ReceiptLongOutlined color="primary" /></ListItemIcon>
                            <ListItemText
                                primary={<b>1. 管理会社・オーナーからの通知</b>}
                                secondary="更新期日の1～3か月前に、書面やメールで「契約更新のお知らせ」が届きます。"
                            />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><QuestionAnswerOutlined color="primary" /></ListItemIcon>
                            <ListItemText
                                primary={<b>2. 条件確認と相談</b>}
                                secondary="家賃・更新料・新条件など内容をしっかり確認し、疑問点は管理会社へ質問しましょう。"
                            />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><CheckCircleOutline color="primary" /></ListItemIcon>
                            <ListItemText
                                primary={<b>3. 継続・退去の意思表示</b>}
                                secondary="契約更新するか、退去するかを期限までに伝える必要があります。"
                            />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><GavelOutlined color="primary" /></ListItemIcon>
                            <ListItemText
                                primary={<b>4. 契約書締結・必要書類提出</b>}
                                secondary="継続の場合は新しい契約書への署名や、必要に応じて保証人・保険の更新も。"
                            />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><TrendingUpOutlined color="primary" /></ListItemIcon>
                            <ListItemText
                                primary={<b>5. 更新料・手数料の支払い</b>}
                                secondary="多くの場合、家賃1か月分前後の更新料や事務手数料が発生します。"
                            />
                        </ListItem>
                    </List>
                </Box>
                <Paper elevation={1} sx={{ p: 2, bgcolor: '#f0f4c3', borderRadius: 2, mb: 2 }}>
                    <Typography variant="body2" fontWeight="bold">
                        <WarningAmberOutlined sx={{ verticalAlign: 'middle', mr: 1 }} />
                        <b>Point!</b> 更新の意思表示は「期限厳守」。うっかり忘れると自動更新やペナルティになることも！
                    </Typography>
                </Paper>
            </Box>

            {/* 家賃交渉のコツ */}
            <Box id="negotiation" sx={{ mb: 5 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    家賃交渉のコツ
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="body1" sx={{ mb: 1 }}>
                                更新時は、<b>家賃や条件の交渉がしやすいタイミング</b>です。実際に成功した例や、交渉の切り口を紹介します。
                            </Typography>
                            <List>
                                <ListItem>
                                    <ListItemIcon><ArrowForwardIos color="primary" /></ListItemIcon>
                                    <ListItemText
                                        primary={<b>相場チェック</b>}
                                        secondary="近隣の同条件物件と比較して、割高なら根拠を示して交渉。"
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><ArrowForwardIos color="primary" /></ListItemIcon>
                                    <ListItemText
                                        primary={<b>長期入居者アピール</b>}
                                        secondary="「トラブルなく住み続けている」実績を伝え、家賃据え置き・減額を相談。"
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><ArrowForwardIos color="primary" /></ListItemIcon>
                                    <ListItemText
                                        primary={<b>条件追加・設備改善交渉</b>}
                                        secondary="家賃据え置きが難しい場合は、設備の改善やサービス追加を持ちかける。"
                                    />
                                </ListItem>
                            </List>
                        </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <img
                            src="/images/blog/rent-negotiation_roomshare-keiyaku-koushin-point.jpg"
                            alt="落ち着いた雰囲気の管理会社オフィスで、ルームシェアの代表者が担当者と和やかに家賃交渉をしている場面。デスクには契約書と資料が並び、双方がメモを取りながら話し合いをしている様子が伝わる"
                            style={{ width: '100%', borderRadius: 8, objectFit: 'cover' }}
                        />
                    </Grid>
                </Grid>
                <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                        家賃交渉時のチェックリスト
                    </Typography>
                    <List dense>
                        <ListItem>
                            <ListItemIcon><CheckCircleOutline color="success" /></ListItemIcon>
                            <ListItemText primary="周辺相場をリサーチ" />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><CheckCircleOutline color="success" /></ListItemIcon>
                            <ListItemText primary="自分たちの居住実績・信頼性をアピール" />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><CheckCircleOutline color="success" /></ListItemIcon>
                            <ListItemText primary="交渉は早めに（余裕を持って連絡）" />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><CheckCircleOutline color="success" /></ListItemIcon>
                            <ListItemText primary="結果は必ず書面で確認" />
                        </ListItem>
                    </List>
                </Box>
                <Paper elevation={0} sx={{ p: 2, bgcolor: '#e3f2fd', borderRadius: 2, mt: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                        <b>交渉の例文：</b>「近隣の同条件物件が◯万円安くなっているため、ご相談させていただけないでしょうか？」
                    </Typography>
                </Paper>
            </Box>

            {/* 退去・継続の判断 */}
            <Box id="decision" sx={{ mb: 5 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    退去か継続か？判断のポイント
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={7}>
                        <Typography variant="body1" sx={{ mb: 2 }}>
                            更新時は「このまま住み続けるべきか？」を冷静に見直すタイミングでもあります。<b>以下の観点</b>から総合的に判断しましょう。
                        </Typography>
                        <List>
                            <ListItem>
                                <ListItemIcon><PeopleAltOutlined color="primary" /></ListItemIcon>
                                <ListItemText
                                    primary={<b>ルームメイトとの関係</b>}
                                    secondary="生活リズムや価値観の変化、トラブルの有無を振り返る"
                                />
                            </ListItem>
                            <ListItem>
                                <ListItemIcon><HomeOutlined color="primary" /></ListItemIcon>
                                <ListItemText
                                    primary={<b>物件・設備の満足度</b>}
                                    secondary="老朽化や修繕の必要、立地や環境の変化をチェック"
                                />
                            </ListItem>
                            <ListItem>
                                <ListItemIcon><TrendingUpOutlined color="primary" /></ListItemIcon>
                                <ListItemText
                                    primary={<b>家賃やコストの妥当性</b>}
                                    secondary="更新料や新家賃を含め、負担が増えていないか見直す"
                                />
                            </ListItem>
                            <ListItem>
                                <ListItemIcon><ReceiptLongOutlined color="primary" /></ListItemIcon>
                                <ListItemText
                                    primary={<b>将来設計・ライフステージの変化</b>}
                                    secondary="就職・転職・結婚など、今後の予定も踏まえて検討"
                                />
                            </ListItem>
                        </List>
                        <Paper elevation={0} sx={{ p: 2, bgcolor: '#fffde7', borderRadius: 2, mt: 2 }}>
                            <Typography variant="body2">
                                <b>迷ったときは？</b> ルームメイト同士で率直に話し合い、<Box component="span" sx={{ fontWeight: 'bold', color: 'primary.main' }}>「これからの暮らし方」</Box>について意見を共有しましょう。
                            </Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} md={5}>

                    </Grid>
                </Grid>
            </Box>

            {/* 更新トラブル回避 */}
            <Box id="trouble" sx={{ mb: 5 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    契約更新トラブルを回避するには
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body1" sx={{ mb: 2 }}>
                    契約更新時は、<b>思わぬトラブル</b>が発生することも。あらかじめ<Chip label="注意" color="warning" size="small" sx={{ mx: 1 }} />したいポイントを押さえておきましょう。
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <List>
                            <ListItem>
                                <ListItemIcon><WarningAmberOutlined color="warning" /></ListItemIcon>
                                <ListItemText
                                    primary={<b>更新料・手数料の確認不足</b>}
                                    secondary="知らない間に高額な更新料・手数料が追加されるケースも。必ず事前に総額を確認。"
                                />
                            </ListItem>
                            <ListItem>
                                <ListItemIcon><WarningAmberOutlined color="warning" /></ListItemIcon>
                                <ListItemText
                                    primary={<b>契約条件の不利益変更</b>}
                                    secondary="家賃大幅値上げや禁止事項の追加など、納得できない場合は必ず相談・交渉を。"
                                />
                            </ListItem>
                            <ListItem>
                                <ListItemIcon><WarningAmberOutlined color="warning" /></ListItemIcon>
                                <ListItemText
                                    primary={<b>ルームメイト間の意思疎通不足</b>}
                                    secondary="代表者だけで決めず、全員の同意を確認して進める。"
                                />
                            </ListItem>
                            <ListItem>
                                <ListItemIcon><WarningAmberOutlined color="warning" /></ListItemIcon>
                                <ListItemText
                                    primary={<b>退去申し出の遅れ</b>}
                                    secondary="退去を決めたら早めに管理会社へ連絡。ギリギリだと追加費用やトラブルのもとに。"
                                />
                            </ListItem>
                        </List>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Paper elevation={1} sx={{ p: 2, bgcolor: '#fce4ec', borderRadius: 2 }}>
                            <Typography variant="subtitle2" fontWeight="bold" color="error" sx={{ mb: 1 }}>
                                更新トラブル事例
                            </Typography>
                            <List dense>
                                <ListItem>
                                    <ListItemIcon><WarningAmberOutlined color="error" /></ListItemIcon>
                                    <ListItemText
                                        primary="代表者がこっそり更新し、他メンバーが知らないまま条件変更に同意していた"
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><WarningAmberOutlined color="error" /></ListItemIcon>
                                    <ListItemText
                                        primary="退去連絡が遅れ、翌月分家賃も請求された"
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><WarningAmberOutlined color="error" /></ListItemIcon>
                                    <ListItemText
                                        primary="家賃値上げを理由にトラブルが起き、関係が悪化"
                                    />
                                </ListItem>
                            </List>
                        </Paper>
                    </Grid>
                </Grid>
                <Paper elevation={0} sx={{ p: 2, bgcolor: '#e3f2fd', borderRadius: 2, mt: 3 }}>
                    <Typography variant="body2" color="text.secondary">
                        <b>アドバイス：</b>「何をいつまでにするか」一覧を全員で共有し、LINEグループやチャットツールで進捗管理するのが効果的です。
                    </Typography>
                </Paper>
            </Box>

            {/* Q&A */}
            <Box id="faq" sx={{ mb: 5 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    よくある質問・Q&A
                </Typography>
                <Divider sx={{ my: 2 }} />
                <List>
                    <ListItem>
                        <ListItemIcon><QuestionAnswerOutlined color="primary" /></ListItemIcon>
                        <ListItemText
                            primary={<b>Q. 契約更新に必要な書類は？</b>}
                            secondary="A. 通常は新契約書、住民票、保証人関連書類、保険証明書などが求められます。管理会社へ事前確認を。"
                        />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon><QuestionAnswerOutlined color="primary" /></ListItemIcon>
                        <ListItemText
                            primary={<b>Q. 更新料や手数料は必ず払う必要がある？</b>}
                            secondary="A. 契約書に記載があれば支払い義務あり。納得できない場合は交渉や相談を。"
                        />
                    </ListItem>

                    <ListItem>
                        <ListItemIcon><QuestionAnswerOutlined color="primary" /></ListItemIcon>
                        <ListItemText
                            primary={<b>Q. 家賃値上げを断れますか？</b>}
                            secondary="A. 納得できない場合は根拠をもとに交渉可能。難しい場合は退去も選択肢に。"
                        />
                    </ListItem>
                </List>
            </Box>

            {/* まとめ */}
            <Box id="summary" sx={{ mb: 6 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    まとめ
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ p: 2, bgcolor: '#f1f8e9', borderRadius: 2 }}>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        ルームシェアの契約更新は、<b>新しい暮らしの再スタート</b>。トラブルを未然に防ぎ、納得のいく条件で次の一年を迎えるために—
                    </Typography>
                    <List dense>
                        <ListItem>
                            <ListItemIcon><CheckCircleOutline color="success" /></ListItemIcon>
                            <ListItemText primary="早めの準備と情報共有が大切" />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><CheckCircleOutline color="success" /></ListItemIcon>
                            <ListItemText primary="家賃や条件は納得いくまで相談・交渉" />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><CheckCircleOutline color="success" /></ListItemIcon>
                            <ListItemText primary="疑問や変更点は必ず書面で確認" />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><CheckCircleOutline color="success" /></ListItemIcon>
                            <ListItemText primary="ルームメイト全員で話し合い、協力して進める" />
                        </ListItem>
                    </List>
                    <Typography variant="body2" color="text.secondary">
                        この記事が、あなたのルームシェア生活がさらに快適で安心なものになる手助けとなれば幸いです。
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
};

const post = {
    id: 'roomshare-keiyaku-koushin-point',
    slug: 'roomshare-keiyaku-koushin-point',
    title: 'ルームシェアの「契約更新」時に注意すべきポイント',
    excerpt: '契約更新の流れ 家賃交渉のコツ 退去・継続の判断 更新トラブル回避',
    coverImage: '/images/blog/cover-roomshare-renewal_roomshare-keiyaku-koushin-point.jpg',
    coverImageAlt: '明るい自然光が差し込むモダンなルームシェアの共有リビングで、多様な背景を持つルームメイトたちが楽しく会話をしている様子。リビングの中央には大きなテーブルがあり、カラフルなソファや観葉植物が配置され、和やかで協力的な雰囲気が伝わる。記事のテーマである契約更新の前向きなイメージを象徴している。',
    publishedAt: '2024-06-19T09:00:00+09:00',
    isPublished: true,
    categories: ['ルームシェア', '契約・法律', '暮らしの知恵'],
    tags: ['契約更新', '家賃交渉', 'トラブル対策', '退去', 'ルームメイト'],
    author: {
        name: '川島 未来',
        avatar: '/images/avatars/kawashima-mirai.jpg',
        bio: 'シェアハウス運営とルームシェア生活に精通し、実体験と最新トレンドをもとに分かりやすく解説。住まいの選び方やトラブル防止にも詳しい。',
        profilePicture: '/images/avatars/kawashima-mirai.jpg'
    },
    viewCount: 1000,
    contentComponent: PostContent
};

export default post;